import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaSave } from 'react-icons/fa';
import { settingsAPI } from '../services/api';
import { REPORTS, PROFILE_FIELDS, SETTING_KEY, loadReportProfiles, getStoredProfiles } from '../utils/reportProfiles';
import FloatingScrollWrapper from './FloatingScrollWrapper';

// App Configuration → Reports & Documents: header / footer of every report.
// The "All reports" row is the default; a blank cell in a report's row uses it.
const ReportProfilesEditor = () => {
  const [profiles, setProfiles] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadReportProfiles().then(() => setProfiles(JSON.parse(JSON.stringify(getStoredProfiles() || {}))));
  }, []);

  const set = (reportKey, field, value) => setProfiles((p) => ({ ...p, [reportKey]: { ...(p[reportKey] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      // Drop empty cells so blank always means "use the default".
      const clean = {};
      Object.entries(profiles).forEach(([key, prof]) => {
        const kept = Object.fromEntries(Object.entries(prof || {}).filter(([, v]) => v !== '' && v !== undefined && v !== null));
        if (Object.keys(kept).length) clean[key] = kept;
      });
      await settingsAPI.updateSetting(SETTING_KEY, JSON.stringify(clean));
      await loadReportProfiles();
      toast.success('Report headers and footers saved');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <div className="card-header" style={{ justifyContent: 'space-between' }}>
        <div>
          <h3 className="card-title">Report Header &amp; Footer — per report</h3>
          <p className="text-muted" style={{ margin: '4px 0 0', fontSize: 'var(--fs-sm)' }}>
            Printed in the header and footer of every printed report and PDF export. Leave a cell blank to use the "All reports" default. Client name, address and logos come from Report Branding below.
          </p>
        </div>
        <button className="btn btn-primary" onClick={save} disabled={saving}><FaSave /> {saving ? 'Saving…' : 'Save report settings'}</button>
      </div>
      <div className="card-body">
        <FloatingScrollWrapper>
          <table style={{ minWidth: '1100px' }}>
            <thead>
              <tr>
                <th style={{ minWidth: '170px' }}>Report</th>
                {PROFILE_FIELDS.map((f) => <th key={f.key} style={{ minWidth: f.key === 'footer_text' ? '240px' : '150px' }}>{f.label}</th>)}
                <th style={{ minWidth: '70px', textAlign: 'center' }}>Logos</th>
              </tr>
            </thead>
            <tbody>
              {REPORTS.map((r) => (
                <tr key={r.key} style={r.key === 'default' ? { background: 'color-mix(in srgb, var(--primary-color) 6%, var(--card-bg))' } : undefined}>
                  <td><strong>{r.label}</strong></td>
                  {PROFILE_FIELDS.map((f) => (
                    <td key={f.key}>
                      {r.key === 'default' && f.key === 'document_name' ? (
                        <span className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>Set per report</span>
                      ) : (
                        <input
                          className="form-control"
                          style={{ fontSize: 'var(--fs-md)', padding: '4px 6px' }}
                          value={profiles[r.key]?.[f.key] || ''}
                          placeholder={r.key === 'default' ? f.placeholder : 'Default'}
                          onChange={(e) => set(r.key, f.key, e.target.value)}
                        />
                      )}
                    </td>
                  ))}
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={profiles[r.key]?.show_logos !== false} onChange={(e) => set(r.key, 'show_logos', e.target.checked ? '' : false)} title="Show client and app logos in this report's header" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </FloatingScrollWrapper>
      </div>
    </div>
  );
};

export default ReportProfilesEditor;
